import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { type AdminSubmitClaimRequestBody, submitInternalClaim } from '~/services/LuxLoyalty';

import { claimCampaignQueryKeys } from './claimCampaignQueryKeys';

interface SubmitInternalClaimParams {
  campaignId: string;
  body: AdminSubmitClaimRequestBody;
}

/**
 * Submits a claim on behalf of a customer and invalidates the claims cache for that campaign.
 */
export function useSubmitInternalClaim() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ campaignId, body }: SubmitInternalClaimParams) => {
      return await submitInternalClaim(campaignId, body);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: claimCampaignQueryKeys.claims(variables.campaignId) });
      queryClient.invalidateQueries({ queryKey: claimCampaignQueryKeys.codes(variables.campaignId) });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error submitting claim: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
  });
}
